import { useQueryClient } from '@tanstack/react-query';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import {
  getGetIntegrationWebhooksQueryKey,
  usePatchIntegrationWebhooksUuid,
  usePostIntegrationWebhooksBulkUpdate
} from 'api';

import { useOrganisationId } from 'hooks/context';

import { bulkOutcomeToast, summarizeBulkUpdate } from './bulkOutcome';
import { REACTIVATE_WEBHOOK_ERROR } from './constants';
import { reactivateWebhookSuccessMessage } from './utils';

export const useReactivateWebhooks = () => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const patchMutation = usePatchIntegrationWebhooksUuid();
  const bulkMutation = usePostIntegrationWebhooksBulkUpdate();

  const reactivate = (uuids: string[], onSuccess: () => void) => {
    const invalidate = () =>
      queryClient.invalidateQueries({
        queryKey: getGetIntegrationWebhooksQueryKey(organisationId)
      });

    const onError = () =>
      openNotification({ kind: 'error', message: REACTIVATE_WEBHOOK_ERROR });

    if (uuids.length === 1) {
      patchMutation.mutate(
        { organisationId, uuid: uuids[0], data: { active: true } },
        {
          onSuccess: () => {
            invalidate();
            openNotification({
              kind: 'success',
              message: reactivateWebhookSuccessMessage(1)
            });
            onSuccess();
          },
          onError
        }
      );
      return;
    }

    bulkMutation.mutate(
      {
        organisationId,
        data: {
          uuids,
          values: { active: true }
        }
      },
      {
        onSuccess: (response) => {
          invalidate();
          openNotification(
            bulkOutcomeToast(summarizeBulkUpdate(response), {
              allSuccess: reactivateWebhookSuccessMessage(uuids.length),
              verb: 'reactivated'
            })
          );
          onSuccess();
        },
        onError
      }
    );
  };

  return {
    reactivate,
    isPending: patchMutation.isPending || bulkMutation.isPending
  };
};
